<template>
  <div class="reporting-tab" :class="{'reporting-tab__print': isStaticView}">
    <div class="reporting-tab__container" v-show="isDataFetched">
      <div class="reporting-tab__row">
        <base-content-block class="reporting-tab__row-item"
                            :isStaticView="isStaticView"
                            :isCollapsed="false"
                            title="Rank Zone Time Progression">
          <div class="reporting-tab__chart-wrapper">
            <brief-area-chart class="reporting-tab__chart"
                              ref="chart"
                              :isDataUpdating="isDataUpdating"
                              :isScreenshotTaking="isScreenshotTaking"
                              :snapshots="snapshotsData"
                              :selectedType="tableType"
                              :urlId="briefContent.url_id"
                              @updateData="getData(true)"
                              @web-property="onWebPropertyGroupChanged" />
          </div>
          <template #title-additional>
            <base-screenshot :id="'area-chart'"
                             :name="screenshotName"
                             class="reporting-tab__screenshot"
                             @screenshot="onScreenShotStatusChanged"/>
          </template>
        </base-content-block>
      </div>

      <BaseCollapsibleBlock :title="rzComparisonTitle"
                            :isCollapsed="false"
                            :isStaticView="isStaticView"
                            isDividerHidden>
        <template #header-additional>
          <div class="reporting-tab__table-type-selection">
            <base-radio
              v-model="tableType"
              style="margin-right: 15px;"
              value="msv"
              label="MSV"
              class="reporting-tab__table-type-selection__radio-button"
              crossColor="#8F90A6"/>
            <base-radio
              v-model="tableType"
              value="keyword"
              label="# KW"
              class="reporting-tab__table-type-selection__radio-button"
              crossColor="#8F90A6"/>
          </div>
        </template>
        <RankZone ref="rank-zone"
                  :tableType="tableType"
                  :webPropertyGroupSelected="webPropertyGroupSelected"
                  :selected-advanced-options="selectedAdvancedOptions"
                  :advancedFilters="advancedFilters"
        />
      </BaseCollapsibleBlock>

      <Keywords :keywords="keywords"
                :title="keywordTitle"
                :webPropertyGroupId="webPropertyGroupSelected._id"
                :isStaticView="isStaticView"
                :isKeywordsPresent="isKeywordsPresent"
                @update-score="$emit('update-score', $event)"/>
    </div>
<!--    <div class="container" v-if="!isDataFetched && showLoader">-->
<!--      <div class="rank-zone__loader">-->
<!--        <base-loader/>-->
<!--      </div>-->
<!--    </div>-->
<!--    <div class="reporting-tab__update-loader" v-else-if="isDataUpdating && showLoader">-->
<!--      <base-loader class="reporting-tab__update-loader-ring"/>-->
<!--    </div>-->
  </div>
</template>

<script>

import _ from 'lodash'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import filters from '@/common/Filters'
import { convertAdvancedOptionsToFilters } from '@/features/reports/common/advanced-options/config'
import BriefAreaChart from './BriefAreaChart'
import ContentBrief from './index'
import Keywords from './Keywords'

/**
 * Reporting tab. Used in {@link ContentBrief}
 * Pay attention: unlike from other components in which parent component triggers api calls and sends data to child
 * here as trigger for api calls acts {@link RankZone}
 */
export default {
  name: 'Reporting',
  components: {
    Keywords,
    // to avoid circular dependencies https://vuejs.org/v2/guide/components-edge-cases.html#Circular-References-Between-Components
    RankZone: () => import('./RankZone'),
    BriefAreaChart
  },
  props: {
    isStaticView: {
      type: Boolean,
      default: false
    },
    /**
     * Advanced options from parent element
     */
    selectedAdvancedOptions: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      // loader flags controls by RankZone component
      isDataUpdating: false,
      isDataFetched: false,
      isScreenshotTaking: false,
      tableType: 'msv',
      webPropertyGroupSelected: {},
      snapshotsData: []
    }
  },
  watch: {
    'briefContent.progress': {
      handler (value) {
        const snapshotsData = Object.values(_.cloneDeep(value)).filter(item => item)
        if (!this.isSameSnapshotsData(snapshotsData, this.snapshotsData)) {
          this.snapshotsData = snapshotsData
        }
      },
      immediate: true
    },
    isSnapshotsLoaded: {
      handler(value, oldValue){
        if(!value){
          return
        }
        this.isDataFetched = false
      },
      immediate: true
    },
    webPropertyGroups: {
      handler (v) {
        if (!v || !v.length) {
          return
        }
        this.webPropertyGroupSelected = v[0]
      },
      immediate: true,
      deep: true
    },
    selectedAdvancedOptions: {
      handler () {
        this.$nextTick(() => {
          if (this.isSnapshotsLoaded) {
            this.getData()
          }
        })
      },
      deep: true
    },
    webPropertyGroupSelected: {
      handler (v) {
        if (!v) {
          return
        }
        this.getData()
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('rankZoneCommon', {
      webPropertyGroups: (state) => state.webPropertyGroups,
      isSnapshotsLoaded: 'isSnapshotsLoaded'
    }),
    ...mapState('briefContent', ['briefContent']),
    isKeywordsPresent () {
      return !!(this.briefContent.keywords && this.briefContent.keywords.length)
    },
    showLoader () {
      return this.briefContent.name
    },
    competitorSiblingName () {
      return this.webPropertyGroupSelected.type.toLowerCase() !== 'clients'
        ? `(${this.webPropertyGroupSelected.label})`
        : null
    },
    rzComparisonTitle () {
      return `Rank Zone Comparison${this.competitorSiblingName ? ` <span style="font-size: 14px;">${this.competitorSiblingName}</span>` : ''}`
    },
    keywordTitle () {
      return `Keywords${this.competitorSiblingName ? ` <span style="font-size: 14px;">${this.competitorSiblingName}</span>` : ''}`
    },
    keywords (){
      return this.briefContent.keywords
    },
    screenshotName () {
      if (!this.clientName) {
        return ''
      }
      return `${this.clientName}_ContentBrief_${this.briefContent.name}_Progress_${moment(Date.now()).format('YYYY-MM-DD')}`
    }
  },
  methods: {
    ...mapActions('briefContent', [
      'getProgressData',
      'resetProgressData'
    ]),
    getValue (item, cell) {
      return item[cell.propName] ? item[cell.propName] : 0
    },
    onScreenShotStatusChanged (status) {
      this.isScreenshotTaking = status
    },
    /**
     * check if new data is differs from old data
     * @param input - new data set
     * @param existing - old data set
     * @returns {boolean}
     */
    isSameSnapshotsData (input = [], existing = []) {
      if (existing.length !== input.length) {
        return false
      }
      const inputDates = input.map(item => item.label)
      const existingDates = existing.map(item => item.label)
      return inputDates.every(item => existingDates.includes(item))
    },
    async getData (isOnlyProgress = false) {
      this.isDataUpdating = true
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      await this.getProgressData({
        clientId: this.clientId,
        filters: { advancedFilters, url_id: this.briefContent.url_id, web_property_group_id: this.webPropertyGroupSelected._id }
      })
      this.isDataFetched = true
      this.isDataUpdating = false
      !isOnlyProgress && this.$refs['rank-zone'].getData({ initial: true, advancedFilters })
    },
    onWebPropertyGroupChanged (group) {
      if (group.value === this.webPropertyGroupSelected._id) {
        return
      }
      this.webPropertyGroupSelected = this.webPropertyGroups.find(item => item._id === group.value) || {}
    }
  },
  filters: { ...filters }
}

</script>

<style scoped lang="scss">

  .reporting-tab {
    margin-top: 14px;
    margin-bottom: -20px;
    &__container{
      min-width: 964px;
    }

    &__table-type-selection {
      display: flex;
      align-items: center;
      margin-left: auto;
      margin-right: 44px;

      &__radio-button {
        cursor: pointer;

        &::v-deep .radio__label {
          font-size: 18px;
          font-weight: 500;
        }
      }
    }

    &__no-data {
      height: 90px;
    }

    &__screenshot {
      font-size: 18px;
      cursor: pointer;

      &:hover {
        color: $rfx-bright-turquoise;
      }
    }

    &__row {
      display: flex;
      align-items: stretch;
      justify-content: space-between;

      &-item {
        flex: 1 1 100%;

        &:first-child {
        }
      }
    }

    &__chart-wrapper {
      width: 100%;
      padding-top: 20px;
      box-sizing: border-box;
    }

    &__chart {
      padding: 10px 10px 0;
    }

    &__update-loader {
      position: fixed;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      background-color: white;
      top: 0;
      left: 0;
      opacity: 0.5;
      z-index: 10000;
    }

    &__update-loader-ring {
      margin-top: 300px;
    }
    &__not-found {
      font-size: 27px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 60px;
      color: $rfx-medium-grey;
    }
  }

  .reporting-tab__print {
    padding-bottom: 0;

    .container {
      width: calc(100% - 60px);
    }
  }

  @media print {
    @page {
      margin: 0;
      size: auto;
    }
  }

</style>
